import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { Route, Routes } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import { TourProvider } from '@reactour/tour';
import ThemeContext from '../contexts/themeContext';
import { Dots } from 'loading-animations-react';

import Aside from '../layout/Aside/Aside';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { demoPages, layoutMenu } from '../menu';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import './App.css';
import { useDispatch, useSelector } from 'react-redux';
import AuthProviderGate from "../services/auth/AuthProviderGate";
import CrispWidget from '../components/CrispWidget';
import { getMe } from '../redux/slices/UserSlice';

const App = () => {
	getOS();
	const dispatch = useDispatch();
	const me = useSelector((state) => state.user.me);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		} else {
			document.documentElement.removeAttribute('theme');
		}
	}, [darkModeStatus]);

	useEffect(() => {
		(async () => {
			const me = await dispatch(getMe());
			console.log("User is now :", me);
		})();
	}, []);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	//	Add paths to the array that you don't want to be "Aside".
	const withOutAsidePages = [demoPages.login.path, demoPages.signUp.path, layoutMenu.blank.path];

	return (

		<>
			<AuthProviderGate>
				<ThemeProvider theme={theme}>
					<ToastProvider components={{ ToastContainer, Toast }}>
						<TourProvider
							steps={steps}
							styles={styles}
							showNavigation={false}
							showBadge={false}>
							{
								me?.loading ? <div style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									height: '100vh',
									width: '100%',
								}}>
									<Dots />
								</div> : <div
									ref={ref}
									className='app'
									style={{
										backgroundColor: fullScreenStatus && 'var(--bs-body-bg)',
										zIndex: fullScreenStatus && 1,
										overflow: fullScreenStatus && 'scroll',
									}}>
									<Routes>
										{withOutAsidePages.map((path) => (
											<Route key={path} path={path} />
										))}
										<Route path='*' element={<Aside />} />
									</Routes>
									<Wrapper />
								</div>
							}
							<Portal id='portal-notification'>
								<ReactNotifications />
							</Portal>
						</TourProvider>
					</ToastProvider>
				</ThemeProvider>

			</AuthProviderGate>
			<CrispWidget />
		</>
	);
};

export default App;
